import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  RefreshCw, CheckCircle2, ArrowRight, ArrowLeft,
  Users, MessageSquare, Target, Shield, Heart, TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const adkarModel = [
  {
    letter: 'A',
    word: 'Awareness',
    description: 'Understanding why the change is necessary',
    questions: ['Why is this change happening?', 'What happens if we don\'t change?', 'What\'s wrong with the current state?'],
    actions: ['Communicate the business reasons for change', 'Share data that supports the need for change', 'Address the "what\'s in it for me" question']
  },
  {
    letter: 'D',
    word: 'Desire',
    description: 'Personal motivation to support and participate in the change',
    questions: ['What motivates individuals to change?', 'What resistance exists?', 'How can we build commitment?'],
    actions: ['Engage influential leaders as change champions', 'Address personal concerns and fears', 'Highlight benefits for individuals and teams']
  },
  {
    letter: 'K',
    word: 'Knowledge',
    description: 'Information about how to change',
    questions: ['What new skills are required?', 'What training is needed?', 'How will people learn the new way?'],
    actions: ['Develop comprehensive training programs', 'Create job aids and reference materials', 'Provide hands-on practice opportunities']
  },
  {
    letter: 'A',
    word: 'Ability',
    description: 'Capability to implement the change on a day-to-day basis',
    questions: ['Can people perform the new tasks?', 'What barriers prevent implementation?', 'Is there enough time to practice?'],
    actions: ['Provide coaching and support during transition', 'Remove obstacles that prevent adoption', 'Allow time for skill development']
  },
  {
    letter: 'R',
    word: 'Reinforcement',
    description: 'Factors that sustain the change',
    questions: ['How do we prevent regression?', 'What recognition exists for adopting change?', 'Are systems aligned with the new way?'],
    actions: ['Celebrate wins and recognize early adopters', 'Align incentives with new behaviors', 'Measure and report on adoption metrics']
  },
];

const changePhases = [
  {
    title: 'Prepare for Change',
    icon: Target,
    tasks: [
      { task: 'Define the change', detail: 'Clearly articulate what is changing, why, and what success looks like.' },
      { task: 'Assess organizational readiness', detail: 'Evaluate the organization\'s capacity and willingness to change.' },
      { task: 'Identify impacted groups', detail: 'Map all stakeholders and understand how each group will be affected.' },
      { task: 'Build the change team', detail: 'Assemble a team with the right skills, authority, and credibility.' },
      { task: 'Develop sponsor roadmap', detail: 'Ensure executives are prepared to visibly and actively support the change.' },
    ]
  },
  {
    title: 'Manage the Change',
    icon: Users,
    tasks: [
      { task: 'Develop change management plans', detail: 'Create communication, training, coaching, and resistance management plans.' },
      { task: 'Execute communication plan', detail: 'Deliver consistent messages through multiple channels over time.' },
      { task: 'Deliver training', detail: 'Provide knowledge and skills people need to change successfully.' },
      { task: 'Manage resistance', detail: 'Identify root causes of resistance and address them proactively.' },
      { task: 'Engage managers', detail: 'Equip people managers to support their teams through the transition.' },
      { task: 'Track adoption', detail: 'Monitor whether people are actually changing their behaviors.' },
    ]
  },
  {
    title: 'Reinforce the Change',
    icon: Shield,
    tasks: [
      { task: 'Collect feedback', detail: 'Gather input from employees about what\'s working and what\'s not.' },
      { task: 'Diagnose gaps', detail: 'Identify where adoption is lagging and why.' },
      { task: 'Implement corrective actions', detail: 'Address barriers and resistance that emerge during implementation.' },
      { task: 'Recognize success', detail: 'Publicly acknowledge individuals and teams who embrace the change.' },
      { task: 'Transfer ownership', detail: 'Transition change management to business-as-usual operations.' },
      { task: 'Review lessons learned', detail: 'Document what worked and what to improve for future changes.' },
    ]
  },
];

const resistanceTypes = [
  { type: 'Fear of unknown', sign: '"I don\'t understand what this means for me"', response: 'Provide clear, specific information about what will and won\'t change' },
  { type: 'Loss of control', sign: '"No one asked my opinion"', response: 'Involve people in decisions where possible; explain constraints where not' },
  { type: 'Competence concerns', sign: '"I won\'t be able to do my job"', response: 'Provide training, practice time, and ongoing support' },
  { type: 'Past failures', sign: '"We tried this before and it didn\'t work"', response: 'Acknowledge past experiences; explain what\'s different this time' },
  { type: 'Timing issues', sign: '"We have too much going on already"', response: 'Prioritize changes; provide resources to manage workload' },
  { type: 'Cultural conflict', sign: '"That\'s not how we do things here"', response: 'Connect the change to existing values; engage cultural influencers' },
];

const successFactors = [
  { factor: 'Active and visible executive sponsorship', impact: 'The #1 contributor to change success' },
  { factor: 'Dedicated change management resources', impact: '6x more likely to meet objectives' },
  { factor: 'Structured change management approach', impact: '3.5x more likely to stay on schedule' },
  { factor: 'Employee engagement and participation', impact: '5x more likely to stay on budget' },
  { factor: 'Frequent and open communication', impact: '4x more likely to achieve benefits' },
  { factor: 'Integration with project management', impact: '2x more likely to sustain results' },
];

const GuideChangeManagementPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="guide-change-management-page">
      <SEO 
        title="Change Management Guide - Leading Organizational Change"
        description="A comprehensive guide to leading organizational change. Learn the ADKAR model, change management phases, and how to overcome resistance to change."
        keywords="change management guide, ADKAR model, organizational change, change leadership, resistance to change, change management process, CCMP"
        url="/resources/guides/change-management"
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link to="/resources/guides" className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back to Guides
          </Link>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full mb-6">
            <RefreshCw className="h-4 w-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-700">Change Management</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-carbon-black mb-4" data-testid="guide-title">
            Change Management Guide
          </h1>
          <p className="text-xl text-slate-gray leading-relaxed">
            A comprehensive guide to leading organizational change. Change is hard—but with the right approach, you can help people move from resistance to adoption and make change stick.
          </p>
        </div>
      </section>

      {/* Why Change Fails */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-bold text-carbon-black mb-6">Why Change Initiatives Fail</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-red-50 rounded-xl">
              <div className="text-3xl font-bold text-red-600 mb-2">70%</div>
              <p className="text-sm text-slate-gray">of change initiatives fail to achieve their goals</p>
            </div>
            <div className="text-center p-6 bg-amber-50 rounded-xl">
              <div className="text-3xl font-bold text-amber-600 mb-2">#1</div>
              <p className="text-sm text-slate-gray">reason for failure: employee resistance and lack of support</p>
            </div>
            <div className="text-center p-6 bg-green-50 rounded-xl">
              <div className="text-3xl font-bold text-green-600 mb-2">6x</div>
              <p className="text-sm text-slate-gray">more likely to succeed with excellent change management</p>
            </div>
          </div>
          <p className="text-slate-gray mt-8 leading-relaxed">
            Most change failures aren't due to bad strategy or poor technology—they're people problems. Change management focuses on the human side of change: helping individuals understand, accept, and adopt new ways of working.
          </p>
        </div>
      </section>

      {/* ADKAR Model */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">The ADKAR Model</h2>
            <p className="text-lg text-slate-gray max-w-2xl mx-auto">
              A goal-oriented framework for guiding individual change. Each element must be achieved in order.
            </p>
          </div>
          
          <div className="space-y-6">
            {adkarModel.map((item, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100 shadow-sm">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-purple-500 to-violet-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl font-bold text-white">{item.letter}</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-carbon-black">{item.word}</h3>
                    <p className="text-slate-gray">{item.description}</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4 mt-6">
                  <div className="bg-purple-50 rounded-xl p-4">
                    <h4 className="font-semibold text-purple-700 mb-2">Key Questions</h4>
                    <ul className="space-y-1">
                      {item.questions.map((q, qIdx) => (
                        <li key={qIdx} className="text-sm text-slate-gray flex items-start gap-2">
                          <span className="text-purple-500">•</span> {q}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4">
                    <h4 className="font-semibold text-green-700 mb-2">Actions to Take</h4>
                    <ul className="space-y-1">
                      {item.actions.map((a, aIdx) => (
                        <li key={aIdx} className="text-sm text-slate-gray flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" /> {a}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Change Phases */}
      {changePhases.map((phase, phaseIdx) => {
        const IconComponent = phase.icon;
        return (
          <section key={phaseIdx} className={`py-12 sm:py-16 ${phaseIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-purple-500 to-violet-500 flex items-center justify-center flex-shrink-0">
                  <IconComponent className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black">{phase.title}</h2>
              </div>
              
              <div className="space-y-4 ml-16">
                {phase.tasks.map((item, itemIdx) => (
                  <div key={itemIdx} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-semibold text-carbon-black mb-1">{item.task}</h3>
                        <p className="text-sm text-slate-gray">{item.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* Managing Resistance */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-purple-50 to-violet-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <Heart className="h-12 w-12 text-purple-500 mx-auto mb-4" />
            <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">Managing Resistance</h2>
            <p className="text-lg text-slate-gray max-w-2xl mx-auto">
              Resistance is a natural response to change. Understanding its sources helps you address it effectively.
            </p>
          </div>
          
          <div className="space-y-4">
            {resistanceTypes.map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs font-semibold text-purple-600 uppercase tracking-wider mb-1">Type</div>
                    <div className="font-semibold text-carbon-black">{item.type}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-1">Signs</div>
                    <div className="text-sm text-slate-gray italic">{item.sign}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-1">Response</div>
                    <div className="text-sm text-slate-gray">{item.response}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Factors */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-8 text-center">Key Success Factors</h2>
          
          <div className="grid sm:grid-cols-2 gap-4">
            {successFactors.map((item, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                <div className="flex items-start gap-3">
                  <TrendingUp className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-carbon-black mb-1">{item.factor}</h3>
                    <p className="text-sm text-purple-600">{item.impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-purple-50 to-violet-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Need Help Managing Change?
          </h2>
          <p className="text-lg text-slate-gray mb-8 max-w-2xl mx-auto">
            Our team includes certified change management professionals (CCMP) who can help you navigate complex organizational transitions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/resources/consultation">
              <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all w-full sm:w-auto">
                Book a Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/resources/guides">
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-purple-400 text-purple-600 hover:bg-purple-50 transition-all w-full sm:w-auto">
                More Guides
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default GuideChangeManagementPage;
